import {
    t_analyzed_episodes_ss,
    t_boys,
    t_analyzed_episode_ss
} from '../common/types';
import _round from 'lodash.round';
import { scaleLinear } from 'd3-scale';
import { SPEAKER, isSpeakerHost_short, SPEAKER_SHORT } from '../common/speakers';

export default class Visualizer {
    dataset: t_analyzed_episodes_ss;
    holderDOM: HTMLElement;
    holderMobileDOM: HTMLElement;
    rectHeight: number;
    rectSpacing: number;
    episodeNrWidth: number;
    episodNrMarginRight: number;
    epStatsPaddingLeft: number;
    xScale: d3.ScaleLinear<number, number>;
    canvasWidthMobile: number;

    constructor(holderId: string, dataset: t_analyzed_episodes_ss) {
        this.holderDOM = document.getElementById(holderId);
        this.holderMobileDOM = document.getElementById(holderId + '-mobile');

        this.dataset = dataset;

        this.rectHeight = 12;
        this.rectSpacing = 5;

        this.episodeNrWidth = 60;
        this.episodNrMarginRight = 10;

        this.epStatsPaddingLeft = 30;

        this.xScale = null;
    }

    init() {
        const h = this.calculateChartHeight();
        const html = this.createEpisodesOverview(this.dataset.episodes, h);
        this.holderDOM.innerHTML = html;

        const htmlMobile = this.createHtmlMobile(this.dataset.episodes);
        this.holderMobileDOM.innerHTML = htmlMobile;

        if (window.innerWidth > 600) {
            // This must be done after the initial html has been added to the dom.
            // We need the elements to substract them from the total width for the scale.
            this.calculateScaleForCanvas();
            this.createEpisodesOverviewCanvasses(this.dataset.episodes);
        } else {
            this.calcCanvasWidthMobile();
            this.createEpisodesCanvassesMobile(this.dataset.episodes);
        }
    }

    calculateScaleForCanvas() {
        const widestStats = this.longestEpisodeStatsElement();

        const longestEpisode = this.findLongestEpisode(this.dataset.episodes);

        // Manual offset to fix breaking.
        // Probably forgot a calculation of padding somewhere.
        // too lazy to check, just do manual offset.
        let width = this.holderDOM.clientWidth - widestStats - this.episodeNrWidth + this.episodNrMarginRight - 20;
        this.xScale = scaleLinear()
            .domain([0, longestEpisode.durationSeconds])
            .range([0, width]);
    }

    calcCanvasWidthMobile() {
        const el = document.getElementsByClassName('tt-ep-speakers-m')[0];
        let width = this.holderMobileDOM.clientWidth - el.clientWidth;
        this.canvasWidthMobile = width;
    }

    longestEpisodeStatsElement(): number {
        const els = document.getElementsByClassName('tt-episode-stats');

        let widest = 0;

        // regular for loop
        for (let i = 0; i < els.length; i++) {
            const el = els[i];

            if (el.clientWidth > widest) {
                widest = el.clientWidth;
            }
        }

        return widest;
    }

    createEpisodesOverview(episodes: t_analyzed_episode_ss[], episodeH: number): string {
        let html = '<div class="tt-episodes">';
        for (let i = 0; i < episodes.length; i++) {
            const episode = episodes[i];
            html += this.createEpisodeOverview(episode, episodeH);
        }

        html += '</div>';
        return html;
    }

    createEpisodeOverview(episode: t_analyzed_episode_ss, episodeH: number): string {
        const canvasHeight = this.calculateChartHeight();

        let html = '<div class="tt-episode">';

        /**
         * Episode title.
         */
        let style = [
            `margin-left: ${this.episodeNrWidth + this.episodNrMarginRight}px`
        ];
        html += `
            <div class="tt-episode-title" style="${style.join(';')}">
                ${episode.title}
            </div>
        `;

        // Lower row open tag.
        style = [
            `height: ${canvasHeight}px`
        ];
        html += `<div class="tt-episode-lower-row" style="${style.join(';')}">`;

        /**
         * Episode number.
         */
        style = [
            `width: ${this.episodeNrWidth}px`,
            `margin-right: ${this.episodNrMarginRight}px`
        ];

        html += `
            <div class="tt-episode-overview-nr" style="${style.join(';')}">
                #${episode.id}
            </div>
        `;

        /**
         * Episode canvas element.
         */

        html += `
            <div class="tt-episode-canvas-holder tt-episode-canvas-holder-${episode.id}">
                <span class="tt-ep-duration">${episode.durationHuman}</span>
                <canvas class="tt-episode-canvas-${episode.id}" height="${canvasHeight}"></canvas>
            </div>
        `;

        html += this.createEpisodeLikeStatistics(episode);

        // Lower row close tag.
        html += '</div>';

        html += '</div>';
        return html
    }

    createEpisodeLikeStatistics(episode: t_analyzed_episode_ss): string {
        let html = `<div class="tt-episode-stats">`;

        html += '<div class="tt-ep-stats-speakers">';
        Object.keys(episode.likesPerPerson).forEach(key => {
            let paddingOffset = 0;

            const boy = key as t_boys;

            // Font doesn't align propery. move it manually.
            if (boy == SPEAKER.JOE) {
                paddingOffset = 4;
            }

            const boyLikes = episode.likesPerPerson[boy];
            const pot = _round(boyLikes.percentageOfTotal);
            const text = `${boy.charAt(0).toUpperCase()}: ${boyLikes.count} (${pot}%)`;
            const lps = `avg every ${boyLikes.averageLikeEverySecond}s`;

            let style = [
                `width: ${this.epStatsPaddingLeft + paddingOffset - 5}px`
            ];

            html += `
                <div class="tt-ep-stat-speaker tt-ep-stat-${boy}" style="padding-left: ${this.epStatsPaddingLeft + paddingOffset}px">
                    <div class="tt-ep-stat-connector" style="${style.join(';')}"></div>
                    ${text} - ${lps}
                </div>
            `;

        });
        html += '</div>';

        /**
         * Overall stats.
         */
        html += `
            <div class="tt-ep-stat-all">
                <span>${episode.likeCount} likes.</span>
                <span>avg ${episode.avgLikePerHour} / hour.</span>
            </div>
        `;

        html += '</div>';
        return html;
    }

    createEpisodesOverviewCanvasses(episodes: t_analyzed_episode_ss[]) {
        for (let i = 0; i < episodes.length; i++) {
            const episode = episodes[i];

            // Update the canvas width.
            const width = this.xScale(episode.durationSeconds);
            const canvasEl = document.getElementsByClassName(`tt-episode-canvas-${episode.id}`)[0] as HTMLCanvasElement;
            canvasEl.width = width;
            const ctx = canvasEl.getContext('2d');

            let likes = this.castTimestampToNumber(Object.entries(episode.likes));

            for (let i = 0; i < likes.length; i++) {
                const like = likes[i];

                if (isSpeakerHost_short(like[1])) {
                    this.drawLikeOccurence(ctx, like, this.xScale);
                }
            }
        }
    }

    createEpisodesCanvassesMobile(episodes: t_analyzed_episode_ss[]) {
        for (let i = 0; i < episodes.length; i++) {
            const episode = episodes[i];

            const xScale = scaleLinear()
                .domain([0, episode.durationSeconds])
                .range([0, this.canvasWidthMobile]);

            const canvasEl = document.getElementsByClassName(`tt-episode-canvas-mobile-${episode.id}`)[0] as HTMLCanvasElement;
            canvasEl.width = this.canvasWidthMobile;
            const ctx = canvasEl.getContext('2d');

            let likes = this.castTimestampToNumber(Object.entries(episode.likes));

            for (let i = 0; i < likes.length; i++) {
                const like = likes[i];

                if (isSpeakerHost_short(like[1])) {
                    this.drawLikeOccurence(ctx, like, xScale);
                }
            }
        }
    }

    redrawCanvases() {
        // This must be done after the initial html has been added to the dom.
        // We need the elements to substract them from the total width for the scale.
        this.calculateScaleForCanvas();
        this.createEpisodesOverviewCanvasses(this.dataset.episodes);
    }

    redrawMobileCanvases() {
        this.calcCanvasWidthMobile();

        this.createEpisodesCanvassesMobile(this.dataset.episodes);
    }

    drawLikeOccurence(ctx: CanvasRenderingContext2D, like: [number, SPEAKER_SHORT], xScale: d3.ScaleLinear<number, number>) {
        let rectHeight = this.rectHeight;

        const timestamp = like[0];
        const speaker = like[1];
        let yOffsetDelta = this.getYOffsetForSpeaker(speaker);

        ctx.globalAlpha = 0.4;
        ctx.fillStyle = '#FFF';

        const x = xScale(timestamp);

        ctx.fillRect(x, 0 + yOffsetDelta, 1, rectHeight);

        // // Reset alpha.
        ctx.globalAlpha = 1;
    }

    /**
     * Mobile version.
     * Because mobile real estate is constrained we render a completely different
     * markup
     */
    createHtmlMobile(episodes: t_analyzed_episode_ss[]): string {
        let html = '<div class="tt-episodes-mobile">';
        const canvasHeight = this.calculateChartHeight();

        for (let i = 0; i < episodes.length; i++) {
            const episode = episodes[i];
            html += this.createEpisodeHtmlMobile(episode, canvasHeight);
        }

        html += '</div>';
        return html;
    }

    createEpisodeHtmlMobile(episode: t_analyzed_episode_ss, canvasHeight: number): string {
        let html = '<div class="tt-episode">';

        html += `
            <div class="tt-episode-title tt-episode-title-m">
                #${episode.id}. ${episode.title}
            </div>
        `;

        /**
         * Canvas holder and rest.
         */

        html += `
            <div class="tt-episode-canvas-holder-m tt-episode-canvas-holder-${episode.id}">
                <div class="tt-ep-speakers-m">
        `;

        Object.keys(episode.likesPerPerson).forEach(key => {
            const boy = key as t_boys;

            html += `
                <div class="tt-ep-speaker-m">
                    ${boy.charAt(0).toUpperCase()}:
                </div>
            `;
        });

        html += `
            </div>
            <canvas class="tt-episode-canvas-mobile-${episode.id}" height="${canvasHeight}"></canvas>
        </div>
        `;

        html += `
            <div class="tt-ep-stats-m">
                <div>
                    <span>${episode.likeCount} likes.</span> -
                    <span>avg ${episode.avgLikePerHour} / hour.</span>
                </div>

                <span class="tt-ep-duration">${episode.durationHuman}</span>
            </div>
        `;

        html += '</div>';

        return html;
    }

    getYOffsetForSpeaker(speaker: SPEAKER_SHORT): number {
        switch (speaker) {
            case SPEAKER_SHORT.JOE:
                return 0;

            case SPEAKER_SHORT.CONNOR:
                return this.rectHeight + this.rectSpacing;

            case SPEAKER_SHORT.GARNT:
                return (2 * this.rectHeight) + (2 * this.rectSpacing);

            default:
                return 0;
        }
    }

    calculateChartHeight(): number {
        return (3 * this.rectHeight) + (2 * this.rectSpacing);
    }

    findLongestEpisode(episodes: t_analyzed_episode_ss[]) {
        return episodes.reduce((prev, current) => {

            if (current.durationSeconds > prev.durationSeconds) {
                return current;
            }

            return prev;
        });
    }

    castTimestampToNumber(likes: [string, SPEAKER_SHORT][]): [number, SPEAKER_SHORT][] {
        for (let i = 0; i < likes.length; i++) {
            // @ts-ignore.
            likes[i][0] = parseInt(likes[i][0], 10);
        }

        // @ts-ignore
        return likes;
    }
}